import { defineStore } from "pinia";
import { computed, ref, type Ref } from "vue";
import { AccessRoleTypes, FEATURE_CONFIG, Features } from "@/router/types.ts";
import { ENVIRONMENT_VARS } from "@/global";
import { CUSTOMER_NOT_FOUND_ERROR } from "@/constants/common";
import { isMeteringAdminRole } from "@/features/metering/constants/access";

export type User = any;

export type CustomerApiError = {
  statusCode: number;
  errorMessage: string;
} | null;

export const useUserStore = defineStore("user", () => {
  const user: Ref<User | null> = ref(null);
  const customerApiError: Ref<CustomerApiError> = ref(null);

  const setUser = (value: User): void => {
    user.value = {
      ...value,
      hasIsceWriteAccess: value?.roles?.some((role: string) => role === "ISCE.write"),
      hasIsceReadAccess: value?.roles?.some((role: string) => role === "ISCE.read"),
      hasAdminAccess: value?.roles?.some((role: string) => role === "isce.admin"),
      hasMeteringAdminAccess: isMeteringAdminRole(value?.roles)
    };
  };

  const setPreferences = (preferences: any) => {
    if (!user.value) user.value = {};
    user.value.preferences = preferences;
  };

  const setCustomerApiError = (error: CustomerApiError) => {
    customerApiError.value = error;
  };

  const isCustomerNotFound = computed(() => {
    return (
      customerApiError.value?.statusCode === 403 &&
      customerApiError.value?.errorMessage?.toLowerCase() === CUSTOMER_NOT_FOUND_ERROR
    );
  });

  const preferences = computed(() => user.value?.preferences);

  const customerCode = computed(() => {
    return user.value?.customer?.customerCode ?? user.value?.userToken?.customer_code;
  });

  const isUserInternal = computed(() => {
    return user.value?.userToken?.roles.some((role: string) => role === "MaerskInternal");
  });

  const userEmail = computed((): string => {
    return user.value?.userToken?.email ?? user.value?.profile?.email ?? "";
  });

  const isMeteringAdmin = computed((): boolean => Boolean(user.value?.hasMeteringAdminAccess));

  const checkRequiredRoles = (feature: Features, roleType: AccessRoleTypes): boolean => {
    const config = FEATURE_CONFIG[feature];

    if (!config.requiredRoles?.length) return true;

    const matchingRoles = config.requiredRoles.filter((role: string) => {
      const lowerRole = role.toLowerCase();
      const isAccessTyped = lowerRole.endsWith("read") || lowerRole.endsWith("write");
      return isAccessTyped ? lowerRole.endsWith(roleType.toLowerCase()) : true;
    });

    return user.value?.roles?.some((userRole: string) => matchingRoles.includes(userRole)) ?? false;
  };

  const isFeatureEnabled = (feature: Features, roleType: AccessRoleTypes = AccessRoleTypes.READ): boolean => {
    const config = FEATURE_CONFIG[feature];

    if (!config) return false;

    // Environment variable set in this repo - fallback to Flagsmith feature flag API (isCustomerFeatureFlagEnabled)
    const envFlagValue =
      config.envFlag === null
        ? true
        : (ENVIRONMENT_VARS[config.envFlag] ?? isCustomerFeatureFlagEnabled(config.envFlag));

    if (envFlagValue === false) return false;

    // UserFlag fetched from customer api
    if (config.userFlag) {
      const userFlagValue = user.value?.customer[config.userFlag];
      if (userFlagValue === false) return false;
    }

    // RequiredRole extracted from token provided by IAM
    if (!checkRequiredRoles(feature, roleType)) return false;

    // If all checks pass or are null/undefined/true, return true
    return true;
  };

  // Customer-level feature flags, managed via the 'features' property from the customer API,
  // can be checked using the isCustomerFeatureFlagEnabled method.
  // TODO: Use this method in place of isFeatureEnabled where applicable and replace all hardcoded feature flags.
  const isCustomerFeatureFlagEnabled = (feature: string): boolean => {
    const customerFeatureFlags = user.value?.customer?.features ?? {};
    return Boolean(customerFeatureFlags[feature]);
  };

  const $reset = () => {
    user.value = null;
    customerApiError.value = null;
  };

  const settlementCurrencyEnabled = computed(() => isCustomerFeatureFlagEnabled("enable_settlement_currency"));

  const captainPeterEnabled = computed(() => isCustomerFeatureFlagEnabled("enable_captain_peter"));

  const isBksCustomer = computed(() => {
    return user.value?.customer?.customerType === "BKS";
  });

  // Undefined until the customer call resolves, and absent on records created before
  // the field existed. Consumers (getDataAvailableFrom) treat that as "no horizon" and
  // apply no cutoff, so charts render everything rather than nothing.
  const customerOnboardingDate = computed<string | undefined>(() => {
    return user.value?.customer?.createdAt;
  });

  // BKS customers keep Shipments in Centralized Reporting even when uiEnabled is false
  // (Visibility UI / /shipments stay disabled via isFeatureEnabled).
  const isShipmentsReportingEnabled = computed(() => {
    if (isBksCustomer.value) return true;
    if (!user.value?.customer) return false;
    return isFeatureEnabled(Features.SHIPMENTS);
  });

  const isDndVisibleForBksCustomer = computed(() => {
    if (!user.value?.customer) return false;
    return isFeatureEnabled(Features.DND) || isFeatureEnabled(Features.DND_EXPORT);
  });

  return {
    user,
    setUser,
    customerCode,
    isUserInternal,
    userEmail,
    isMeteringAdmin,
    $reset,
    isFeatureEnabled,
    isCustomerFeatureFlagEnabled,
    setPreferences,
    preferences,
    settlementCurrencyEnabled,
    captainPeterEnabled,
    customerApiError,
    setCustomerApiError,
    isCustomerNotFound,
    isBksCustomer,
    isShipmentsReportingEnabled,
    customerOnboardingDate,
    isDndVisibleForBksCustomer
  };
});
